<template>
  <el-dialog
    :close-on-click-modal="false"
    modal-class="login--form"
    v-model="visible"
    :title="$t(loginType === 'login' ? 'registration.login.title' : 'registration.login.register')"
    width="480"
    :lock-scroll="true"
    align-center
  >
    <el-form ref="formRefs" :model="form" label-width="120px" label-position="top" require-asterisk-position="right" v-if="pageConfig.fields && pageConfig.fields.length > 0">
      <div class="top" v-if="pageConfig && pageConfig.layout && pageConfig.layout.top" v-html="pageConfig.layout.top"></div>

      <template v-for="it in $fields.filter((x) => x.id !== 'PrivacyAgreement')" :key="it.id">
        <el-form-item v-if="(it.id !== 'InvitedCode' && loginType === 'login') || loginType !== 'login'" :label="it.title" :prop="it.id" :rules="it.rules">
          <template v-if="it.id === 'VerificationCode'">
            <div class="verification-code">
              <el-input v-suggest-off v-model="form[it.id]">
                <template #append v-if="captcha.captcha"><img :src="captcha.captcha" @click="handleCaptcha" /></template>
              </el-input>
            </div>
          </template>

          <template v-else-if="it.id === 'DynamicVerificationCode'">
            <div class="verification-code">
              <el-input v-suggest-off type="tel" v-model="form[it.id]">
                <template #append>
                  <el-button type="primary" :disabled="sentTimer.timeout > 0" @click="handleSent">{{ $t('registration.login.sent') }} {{ sentTimer.timeout > 0 ? sentTimer.timeout : '' }}</el-button>
                </template>
              </el-input>
            </div>
          </template>

          <template v-else-if="it.id === 'Mobile'">
            <div style="width: 100%">
              <el-input v-suggest-off type="tel" v-model="form[it.id]"></el-input>
            </div>
          </template>

          <template v-else-if="it.type === 'select'">
            <el-select v-suggest-off v-model="form[it.id]" style="width: 100%" clearable>
              <el-option v-for="it in it.options" :label="it.label" :value="it.value"></el-option>
            </el-select>
          </template>

          <template v-else>
            <div style="width: 100%">
              <el-input v-model="form[it.id]" />
            </div>
          </template>

          <div v-if="it.remark" style="color: #777; font-size: 12px; line-height: 18px; padding-top: 3px">
            {{ it.remark }}
          </div>
        </el-form-item>
      </template>

      <template v-for="it in pageConfig.fields.filter((x) => x.id === 'PrivacyAgreement')" :key="it.id">
        <el-form-item v-if="loginType !== 'login'" :prop="it.id" :rules="[{ required: it.mandatory, message: $t(`registration.form.agreement-required`, it) }]">
          <el-checkbox v-model="form.PrivacyAgreement">
            <div class="privacy-agreement-text" v-if="it.remark" v-html="it.remark"></div>
            <div class="privacy-agreement-text" v-else>
              <template v-if="$language == 0">
                我同意并接受
                <a href="https://www.dataleading.cn/" target="_blank">隐私政策</a>
              </template>
              <template v-else>
                I accept the
                <a href="https://www.dataleading.cn/" target="_blank">Terms & Conditions</a>
              </template>
            </div>
          </el-checkbox>
        </el-form-item>
      </template>

      <div class="buttom" v-if="pageConfig && pageConfig.layout && pageConfig.layout.bottom" v-html="pageConfig.layout.bottom"></div>
    </el-form>
    <template #footer>
      <el-button type="primary" plain @click="visible = false">{{ $t('registration.btn.cancel') }}</el-button>
      <el-button type="primary" @click="handleLogin">{{ $t('registration.btn.ok') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
  import { ref, defineEmits, nextTick, onMounted, inject, computed } from 'vue'
  import { getCaptcha } from '@/api/common'
  import { getPageConfig } from '@/api/config'
  import { login, sent } from '@/api/registration'

  import { useUserStore } from '@/store/modules/user'
  import { ElMessage, ElLoading } from 'element-plus'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  const { t, te } = useI18n()
  const router = useRouter()

  const visible = ref(false)
  const form = ref({})
  const formRefs = ref(null)
  const pageConfig = ref({})
  const captcha = ref({})
  const loginType = ref('')
  const agreement = ref(false)
  const $language = inject('$language')

  const handleCaptcha = async () => {
    const { data } = await getCaptcha()
    captcha.value = data
  }

  const sentTimer = ref({
    instance: null,
    timeout: 0,
  })

  const phoneValidator = (rule, value, callback) => {
    var regex = /^1\d{10}$/
    if (rule.required) {
      if (!value) {
        callback(new Error(t('registration.form.phone-validator')))
        return
      }
    }
    if (value && !regex.test(value)) {
      callback(new Error(t('registration.form.phone-validator')))
    } else {
      callback()
    }
  }

  const emailValidator = (rule, value, callback) => {
    if (rule.required) {
      if (!value) {
        callback(new Error(t('registration.form.email-validator')))
        return
      }
    }
    const regex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
    if (value && !regex.test(value)) {
      callback(new Error(t('registration.form.email-validator')))
    } else {
      callback()
    }
  }

  const $fields = computed(() => {
    let fields = pageConfig.value.fields || []
    return fields.map((it) => {
      let extra = {
        layout: {
          xs: 24,
          sm: 24,
          md: 24,
        },
      }
      //所有字段都可以下拉
      if (((it.id === 'Grades' || it.id === 'Classes') && it.remark) || it.remark&&it.remark.indexOf(';') == 0) {
        let remark = it.remark.split(';').filter((x) => x)
        if (remark.length > 1 || (remark.length && it.remark.indexOf(';') == 0)) {
          extra.type = 'select'
          extra.remark = ''
          extra.options = remark.map((x) => {
            let s = x.split('#')
            if (s.length == 2 && s[0] && s[1]) {
              return {
                label: s[1],
                value: s[0],
              }
            }
            return {
              label: x,
              value: x,
            }
          })
        }
      }
      it.rules = [{ required: it.mandatory, message: t(`registration.form.required`, it) }]
      if (it.id === 'Mobile') {
        it.rules.push({
          validator: phoneValidator,
        })
      }
      if (it.id === 'Email') {
        it.rules.push({
          validator: emailValidator,
        })
      }
      //
      return {
        ...it,
        ...extra,
      }
    })
  })

  onMounted(async () => {
    const { data } = await getPageConfig('login')
    pageConfig.value = data
    for (var g of pageConfig.value.fields) {
      if (g.id === 'DynamicVerificationCode' || g.id === 'VerificationCode' || g.id === 'PrivacyAgreement') {
        g.mandatory = true
      }
    }
    // pageConfig.value.fields.push({ id: 'DynamicVerificationCode', title: '动态验证码', mandatory: true })
    // pageConfig.value.fields.push({ id: 'Mobile', title: 'Mobile' })
    // pageConfig.value.fields.push({ id: 'FirstName', title: '名', mandatory: false })
    // pageConfig.value.fields.push({ id: 'Surname', title: '姓', mandatory: false })
    // pageConfig.value.fields.push({ id: 'InvitedCode', title: 'InvitedCode' })
    // pageConfig.value.fields.push({ id: 'Username', title: 'Username' })
  })

  const handleSent = async () => {
    //
    formRefs.value.validateField(
      pageConfig.value.fields.filter((x) => x.id !== 'DynamicVerificationCode' && x.id !== 'InvitedCode' && x.id !== 'PrivacyAgreement').map((x) => x.id),
      async (valid) => {
        if (!valid) {
          return
        }
        console.log('validateField', valid)
        const loadingInstance = ElLoading.service({ fullscreen: true })
        try {
          if (sentTimer.value.instance) {
            clearInterval(sentTimer.value.instance)
          }
          const { ret } = await sent({ ...form.value, ...{ VerificationCodeToken: captcha.value.token } })
          //
          sentTimer.value.timeout = 60
          sentTimer.value.instance = setInterval(() => {
            sentTimer.value.timeout = sentTimer.value.timeout - 1
          }, 1000)
          await handleCaptcha()
          form.value.VerificationCode = ''
          ElMessage({
            message: t(`registration.login.sent-ok`),
            type: 'success',
          })
        } catch (e) {
          sentTimer.value.timeout = 0
        } finally {
          loadingInstance.close()
        }
      }
    )
  }

  const handleLogin = async () => {
    formRefs.value.validate(async (valid) => {
      if (!valid) {
        return
      }
      const loadingInstance = ElLoading.service({ fullscreen: true })
      try {
        const { ret, data } = await login({ ...form.value, ...{ VerificationCodeToken: captcha.value.token } }, { type: loginType.value })
        const { setToken } = useUserStore()
        setToken(data)
        if (data.to === 'confirmation') {
          router.push({ name: 'personalcenter-confirmation', query: { uid: data.uid } })
          return
        }
        router.push({ name: data.to, query: { uid: data.uid } })
      } catch (e) {
        // ElMessage({
        //   message: e.msg,
        //   type: 'error',
        // })
        nextTick(async () => {
          form.value.VerificationCode = ''
          await handleCaptcha()
          nextTick(() => {
            formRefs.value.clearValidate()
          })
        })
      } finally {
        loadingInstance.close()
      }
    })
  }
  //
  defineExpose({
    show: async (type) => {
      visible.value = true
      loginType.value = type
      if (pageConfig.value && pageConfig.value.fields && pageConfig.value.fields.findIndex((x) => x.id === 'VerificationCode') >= 0) {
        await handleCaptcha()
      }

      nextTick(() => {
        formRefs.value.clearValidate()
      })
    },
  })
</script>

<style lang="scss" scoped>
  .top {
    margin-bottom: 12px;
  }

  .verification-code {
    display: flex;
    width: 100%;

    .el-input {
      flex: 0 0 300px;

      :deep() {
        .el-input-group__append {
          width: 102px;
          display: flex;
          overflow: hidden;
          position: relative;

          img,
          .el-button {
            position: absolute;
            left: 1px;
            right: 1px;
            top: 1px;
            bottom: 1px;
            cursor: pointer;
            box-sizing: border-box;
          }
        }
      }
    }
  }

  .verification-code {
    :deep() {
      .el-button--primary {
        background-color: var(--el-color-primary);
        color: #fff;

        &.is-disabled {
          background-color: var(--el-color-primary-light-3);
          color: #fff;
        }
      }
    }
  }
</style>

<style lang="scss">
  .el-overlay {
    &.login--form {
      > .el-overlay-dialog {
        > .el-dialog {
          border-radius: 8px;
          box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px rgba(0, 0, 0, 0.08), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);
          max-width: calc(100% - 12px);

          > .el-dialog__header {
            margin-bottom: 0;
            width: 100%;
            box-sizing: border-box;
            height: 54px;
            padding: 0 18px;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            background: #ffffff;
            color: #223344;
            margin: 0;
            border-top: solid 1px #e4e9ed;
            border-bottom: solid 1px #e4e9ed;
            font-weight: 600;
            font-size: 16px;
            border-radius: 8px 8px 0 0;
            box-sizing: border-box;

            .el-dialog__title {
              color: #223344;
            }
          }

          > .el-dialog__body {
            margin: 0;
            padding: 20px 24px;
            width: 100%;
            flex: 1;
            overflow: hidden;
            background: #fff;
          }

          > .el-dialog__footer {
            width: 100%;
            height: 58px;
            padding: 0 18px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            background-color: #fff;
            box-shadow: inset 0px 1px 0px #f0f0f0;
            border-radius: 0 0 8px 8px;
          }
        }
      }
    }
  }
</style>
